import { get, post, putApi, del, patch } from "@/shared/utils/fetchApi";

export type WaitingListStatus =
  | "available"
  | "redeemed"
  | "requested"
  | "pending"
  | "revoked"
  | "expired";

export interface WaitingListContact {
  email: string;
  status: WaitingListStatus;
}

export interface ContactsListStatusDef {
  value: WaitingListStatus;
  label: { en: string; pt: string };
  color: "green" | "blue" | "yellow" | "gray" | "red" | "purple" | "orange";
}

export interface ContactsListFieldDef {
  schema_version: string;
  type: "contacts-list";
  label: { en: string; pt: string };
  description: { en: string; pt: string };
  contacts_key: string;
  statuses: ContactsListStatusDef[];
}

export interface AdminSetting {
  id: string;
  type: string;
  fields: ContactsListFieldDef | Record<string, unknown>;
  value?: Record<string, unknown>;
  secure: number;
  created_at?: string;
  updated_at?: string;
}

export interface AdminSettingDetail extends AdminSetting {
  contacts?: WaitingListContact[];
  stats?: Record<string, number>;
}

export interface CreateSettingInput {
  type: string;
  fields?: ContactsListFieldDef | Record<string, unknown>;
  value?: Record<string, unknown>;
  secure?: number;
}

export interface UpdateSettingInput {
  fields?: ContactsListFieldDef | Record<string, unknown>;
  value?: Record<string, unknown>;
}

export class SettingsAdminService {
  static async getSettings(): Promise<AdminSetting[]> {
    // `GET /settings?include_secure=true` (superadmin) array flat incluindo secure.
    // Legacy: `/admin/settings` `{success,data}`.

    return (await get<AdminSetting[]>("/settings?include_secure=true")) ?? [];
  }

  static async getSetting(type: string): Promise<AdminSettingDetail> {
    // `GET /settings/type/:type` flat (setting + contacts + stats se contacts-list).

    return get<AdminSettingDetail>(`/settings/type/${type}`);
  }

  static async createSetting(data: CreateSettingInput): Promise<AdminSetting> {
    // `POST /settings` flat (201).

    return post<AdminSetting>("/settings", data);
  }

  // Sem caller real + sem endpoint v2 por-tipo-com-value (v2 update por id = SettingsService).
  // Mantido no legacy; os updates de `value` reais passam por SettingsService.updateSetting(id).
  static async updateSetting(
    type: string,
    data: UpdateSettingInput,
  ): Promise<AdminSetting> {
    const res = await putApi<{ success: boolean; data: AdminSetting }>(
      `/admin/settings/${type}`,
      data,
    );
    return res.data;
  }

  static async addContact(
    type: string,
    email: string,
    status: WaitingListStatus,
  ): Promise<WaitingListContact> {
    // `POST /settings/:type/contacts` flat (201).

    return post<WaitingListContact>(`/settings/${type}/contacts`, {
      email,
      status,
    });
  }

  static async removeContact(type: string, email: string): Promise<void> {
    // `DELETE /settings/:type/contacts/:email` → 204.

    return del<void>(
      `/settings/${type}/contacts/${encodeURIComponent(email)}`,
    );
  }

  static async updateContactStatus(
    type: string,
    email: string,
    status: WaitingListStatus,
  ): Promise<WaitingListContact> {
    // `PATCH /settings/:type/contacts/:email/status` flat.

    return patch<WaitingListContact>(
      `/settings/${type}/contacts/${encodeURIComponent(email)}/status`,
      { status },
    );
  }
}
